import { Dictionary } from 'src/app/core/helper/util';
import { Category } from './category';
import { Page, PageType } from './page';
import { ServiceHelper } from 'src/app/core/helper/services.helper';

/**
 * Every category belongs to a category group, category group represents
 * the category home page (shop homepage, events home pages, etc).
 */
export enum CategoryType {
    Auction = 1,
    Event = 2,
    Marketplace = 3,
    Sponsor = 4,
    Custom = 5,
    Resource = 6,
    Membership = 7,
    Post = 8
}

/**
 * Category Group will have some pre-defined records, created when a 
 * community is created, i.e. for marketplace, events and sponsors. Then admins
 * can create additional custom category groups. Each group has an associated 
 * home page
 */
export class CategoryGroup extends Page {

    static override className = 'CategoryGroup';
    static override idField = 'categoryGroupId';

    categoryGroupId: number = 0;
    categoryType: CategoryType;
    color: string;
    useOnEvents: boolean;
    useOnPosts: boolean;
    useOnMarketplace: boolean;
    useOnSponsors: boolean;
    useOnResources: boolean;

    // NOT PART OF DATA MODEL
    // these properties are used  on the category side menu component for custom categories
    // dictionary will map child category array by category_id. I.e. each 
    // category could have an array of child categories
    parentCategoriesDic: Dictionary<Category[]> = {};

    // root categories in this category group
    rootCategories: Category[] = [];

    // on dashboard, this will point to current category group path, which when it
    // has the category id or slug appended, will take to that categories setup page
    // ultimately letting you navigate the categories from the category layout page.
    // 
    // in the client, this will be something simpler like /events, /shop or /sponsors
    rootPath: string;

    // used in select drop downs, could possibly be used for side menu as well, which 
    // currently builds the indented hierarchy manually in html
    allCategoriesIndented: Category[] = [];

    constructor(catGroup?: Partial<CategoryGroup>) {
        super();
        Object.assign(this, catGroup);
        super.init();

        // this is what category path looks like in dash, would be different in client,
        // and should probably use slugs, not ids
        //http://localhost:8100/events/event-category/382701/666359/general
        switch (this.categoryType) {
            case CategoryType.Auction:
                this.rootPath = '/auctions/auction-category/' + this.categoryGroupId + '/'; break;
            case CategoryType.Marketplace:
                this.rootPath = '/marketplace/marketplace-category/' + this.categoryGroupId + '/'; break;
            case CategoryType.Sponsor:
                this.rootPath = '/sponsors/sponsor-category/' + this.categoryGroupId + '/'; break;
            case CategoryType.Resource:
                this.rootPath = '/content/resource-category/' + this.categoryGroupId + '/'; break;
            case CategoryType.Membership:
                this.rootPath = '/memberships/membership-category/' + this.categoryGroupId + '/'; break;
            case CategoryType.Post:
                this.rootPath = '/news/post-category/' + this.categoryGroupId + '/'; break;
            default:
                this.rootPath = '/events/event-category/' + this.categoryGroupId + '/'; break;
        }
    }

    // called from Community constructor since it can pass in the categories
    buildHierarchy(categories: Category[]) {
        // map each category into its place in the hierarchy, stored against the 
        // category group
        this.rootCategories = [];
        this.parentCategoriesDic = {};
        categories.forEach(c => {
            
                c.indentedTitle = c.title; 
            
            c.sanitizedFeatureImgUrl = ServiceHelper.sanitizer.bypassSecurityTrustResourceUrl(c.featureImgPath);

            // if this category has no parent category id, then it's a root category
            if (c.parentCategoryId > 0 == false) {
                this.rootCategories.push(c);
            }
            else {
                // else this category has a parent
                // initialize array for this categories parent and add it as child
                if (this.parentCategoriesDic[c.parentCategoryId] == null) {
                    this.parentCategoriesDic[c.parentCategoryId] = [];
                }
                this.parentCategoriesDic[c.parentCategoryId].push(c);
            }
        });

        // and build indented array so can easily be displayed in select
        this.allCategoriesIndented = [];
        this.rootCategories.forEach(rc => {
            this.allCategoriesIndented.push(rc);
            this.addSubCats(rc, 1);
        });

        categories.forEach(c => {
            // if has any child categories, mark as having children 
            if (this.parentCategoriesDic[c.categoryId] != null && this.parentCategoriesDic[c.categoryId].length > 0) {
                c.hasChildren = true;
            }
        });
    }


    private addSubCats(parentCat: Category, depth: number) {
        // add parent category itself to list of child cat ids, since we're just 
        // using it to match filters
        parentCat.allChildCatIds = [];
        parentCat.allChildCatIds.push(parentCat.categoryId)
        if (this.parentCategoriesDic[parentCat.categoryId] != null) {
            this.parentCategoriesDic[parentCat.categoryId].forEach(c => {
                for (let i = 0; i < depth; i++) {
                    c.indentedTitle = ' - ' + c.indentedTitle
                }
                this.allCategoriesIndented.push(c);
                // and recurse
                const addedChildCats = this.addSubCats(c, depth + 1);

                // add all the direct children to allChildCatIds
                parentCat.allChildCatIds.push(c.categoryId);

                // and then any sub children
                addedChildCats.forEach(cc => {
                    parentCat.allChildCatIds.push(cc.categoryId)
                })
            });
            return this.parentCategoriesDic[parentCat.categoryId];
        }
        return [];
    }


    override hasChanges() {
        return CategoryGroup.hasChanges(CategoryGroup);
    }
    override revert() {
        return CategoryGroup.revert(CategoryGroup);
    }

    // ----------------------------------------------------
    // Service-like features from here on down
    // ----------------------------------------------------

    // keep local cache of last accessed object, both a copy that is never modified unless saved,
    // and a 'working copy' that is edited in screens.
    protected static override _cachedServerObj: CategoryGroup; // unmodified until saved
    protected static override _cachedObj: CategoryGroup; // working copy
    protected static override _cacheExpiry: number;

    // since we'll want to cache this ALL list for this community, add cached array to hold
    private static _cachedArray: CategoryGroup[];

    public static clearAll() {
        CategoryGroup._cachedServerObj = undefined;
        CategoryGroup._cacheExpiry = undefined;
        CategoryGroup._cachedObj = undefined;
        CategoryGroup._cachedArray = undefined;
    }

    public static override async load(id: number) {
        return await CategoryGroup.getById(CategoryGroup, id);
    }

    public static override async fetchForPageType(type: PageType) {
        return await CategoryGroup.fetchArray(CategoryGroup, 'categorygroups', `pagetype/${type}`);
    }

    public static async fetchAll(silent: boolean = false, bypassCache: boolean = false) {
        if ( bypassCache == true ) {
            return CategoryGroup.fetchArray(CategoryGroup, null, null, 'json', silent);
        } else {
            return CategoryGroup.fetchArrayAndCache(CategoryGroup, null, null, silent);
        }
    }
}
